import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import Button from '../ui/Button';
import cultivarService from '../../services/cultivar';
import { unwrap, errorMessage } from '../../utils/apiResponse';
import GrowingRegionFields from './GrowingRegionFields';
import { emptyRegionDraft, regionPayload, type GrowingRegionDraft } from './growingRegionDraft';
import type { GrowingRegion } from '../../types/cultivar';
import type { ApiResponse } from '../../types/api';

// A region picker that can create the thing it is picking.
//
// The empty state of this list is not an edge case — an organisation starts
// with whatever global regions exist and none of its own, so the first person
// to record a maturity date meets a picker with nothing in it. Sending them to
// an admin page to come back later loses the date they were about to enter.

interface Props {
  regions: GrowingRegion[];
  value: number;
  onChange: (id: number) => void;
  /** Handed the new region so the owner can fold it into its own list. */
  onCreated: (region: GrowingRegion) => void;
  /** Hides the create affordance for read-only contexts. */
  canCreate?: boolean;
  label?: string;
  disabled?: boolean;
  /**
   * Where a new region is saved. Defaults to the cultivar endpoint, which needs
   * a cultivar seat; the farm pages pass the orchard one, which does not.
   */
  createRegion?: (payload: Partial<GrowingRegion> & { name: string }) => Promise<ApiResponse<GrowingRegion>>;
}

export function GrowingRegionPicker({
  regions,
  value,
  onChange,
  onCreated,
  canCreate = true,
  label = 'Growing region *',
  disabled,
  createRegion = (payload) => cultivarService.createGrowingRegion(payload),
}: Props) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<GrowingRegionDraft>(emptyRegionDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    const result = regionPayload(draft);
    if (!result.ok) { setError(result.error); return; }
    setSaving(true);
    setError(null);
    try {
      const region = unwrap(
        await createRegion(result.payload),
        'Could not create the region'
      );
      onCreated(region);
      onChange(region.id);
      setCreating(false);
      setDraft(emptyRegionDraft);
    } catch (e) {
      setError(errorMessage(e, 'Could not create the region'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className="block text-xs font-medium text-neutral-700">{label}</label>
        {canCreate && !creating && (
          <button
            type="button"
            onClick={() => { setCreating(true); setError(null); }}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-0.5"
          >
            <Plus className="h-3 w-3" /> New region
          </button>
        )}
      </div>

      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="input-professional w-full"
      >
        <option value={0}>
          {regions.length === 0 ? 'No regions yet — add one' : 'Select a region…'}
        </option>
        {regions.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}{r.country ? ` (${r.country})` : ''}{r.is_global ? '' : ' — private'}
          </option>
        ))}
      </select>

      {creating && (
        <div className="mt-3 p-3 rounded-lg border border-primary-200 bg-primary-50/50 space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-semibold text-neutral-900">New growing region</h5>
            <button
              type="button"
              onClick={() => { setCreating(false); setError(null); }}
              className="text-neutral-400 hover:text-neutral-600"
              aria-label="Cancel new region"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {error && (
            <div className="p-2 rounded bg-error-50 text-error-800 text-xs">{error}</div>
          )}

          <GrowingRegionFields value={draft} onChange={setDraft} disabled={saving} compact />

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setCreating(false); setError(null); }} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreate} isLoading={saving}>
              Create region
            </Button>
          </div>
          <p className="text-[11px] text-neutral-500">
            Visible to your organisation only. Shared regions are curated centrally.
          </p>
        </div>
      )}
    </div>
  );
}

export default GrowingRegionPicker;
